import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchPrivateImage, publicImageUrl } from "./api";

const LightboxCtx = createContext(null);

export function LightboxProvider({ children }) {
  const [imgs, setImgs] = useState(null); // {items:[{path, scope}], index}
  const [urls, setUrls] = useState([]);

  const open = useCallback((items, index = 0) => {
    setImgs({ items, index });
  }, []);

  const close = () => { setImgs(null); setUrls([]); };

  useEffect(() => {
    if (!imgs) { setUrls([]); return; }
    let mounted = true;
    (async () => {
      const resolved = await Promise.all(imgs.items.map(async (it) => {
        if (it.url) return it.url;
        if (it.scope === "private") return await fetchPrivateImage(it.path);
        return publicImageUrl(it.path);
      }));
      if (mounted) setUrls(resolved);
    })();
    return () => { mounted = false; };
  }, [imgs]);

  const nav = (dir) => {
    if (!imgs) return;
    const n = imgs.items.length;
    setImgs({ ...imgs, index: (imgs.index + dir + n) % n });
  };

  useEffect(() => {
    if (!imgs) return;
    const h = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") nav(1);
      if (e.key === "ArrowLeft") nav(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line
  }, [imgs]);

  return (
    <LightboxCtx.Provider value={{ open }}>
      {children}
      {imgs && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={close}>
          <button data-testid="lightbox-close" onClick={close} className="absolute top-4 right-4 text-white hover:text-[#EF4444]"><X size={28} /></button>
          {imgs.items.length > 1 && (
            <>
              <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); nav(-1); }} className="absolute left-4 text-white hover:text-[#EF4444]"><ChevronLeft size={40} /></button>
              <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); nav(1); }} className="absolute right-4 text-white hover:text-[#EF4444]"><ChevronRight size={40} /></button>
            </>
          )}
          {urls[imgs.index] ? (
            <img
              src={urls[imgs.index]}
              alt=""
              onClick={(e) => e.stopPropagation()}
              className="max-w-[90vw] max-h-[calc(100dvh-4rem)] object-contain rounded"
              data-testid="lightbox-image"
            />
          ) : <div className="hl-sweep w-40 h-56 rounded-xl" role="status" aria-label="Cargando imagen" data-testid="lightbox-loading" />}
          <div className="absolute bottom-4 text-neutral-500 text-sm">{imgs.index + 1} / {imgs.items.length}</div>
        </div>
      )}
    </LightboxCtx.Provider>
  );
}

export function useLightbox() {
  const ctx = useContext(LightboxCtx);
  if (!ctx) throw new Error("useLightbox outside provider");
  return ctx;
}
